export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'theme'
export const THEME_CHANGE_EVENT = 'portfolio:themechange'

/** Browser UI colours (meta[name=theme-color]), matching the page background. */
export const THEME_COLORS = { light: '#fafafa', dark: '#000000' } as const

export function resolveTheme(
  preference: ThemePreference,
  prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches,
): ResolvedTheme {
  return preference === 'system' ? (prefersDark ? 'dark' : 'light') : preference
}

export function readThemePreference(): ThemePreference {
  const saved = localStorage.getItem(THEME_STORAGE_KEY)
  return saved === 'light' || saved === 'dark' ? saved : 'system'
}

export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference)
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
  root.dataset.theme = resolved
  syncThemeColor(resolved)
  return resolved
}

/** Keeps the browser UI color (meta[name=theme-color]) in step with the theme. */
export function syncThemeColor(resolved: ResolvedTheme) {
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', THEME_COLORS[resolved])
}

export function writeThemePreference(preference: ThemePreference) {
  try {
    if (preference === 'system') {
      localStorage.removeItem(THEME_STORAGE_KEY)
    } else {
      localStorage.setItem(THEME_STORAGE_KEY, preference)
    }
  } catch {
    // Storage can be unavailable in privacy-restricted contexts; still apply.
  }

  const resolved = applyTheme(preference)
  window.dispatchEvent(
    new CustomEvent(THEME_CHANGE_EVENT, {
      detail: { preference, resolved },
    }),
  )
  return resolved
}

export const themeBootstrapScript = `(()=>{try{const t=localStorage.getItem('theme');const d=t==='dark'||(t!=='light'&&matchMedia('(prefers-color-scheme: dark)').matches);const r=document.documentElement;r.classList.toggle('dark',d);r.style.colorScheme=d?'dark':'light';r.dataset.theme=d?'dark':'light';document.querySelector('meta[name=theme-color]')?.setAttribute('content',d?'${THEME_COLORS.dark}':'${THEME_COLORS.light}')}catch{}})()`
